import Paginator from "components/Paginator";
import {ChangeEvent, FC, useEffect, useRef, useState} from "react";
import Talent from "./Talent";
import {useSearchParams} from "react-router-dom";
import useAwaitableModal from "hooks/useAwaitableModal";
import Tooltip from "components/Tooltip";
import useRecruiter from "contexts/User/useRecruiter";
import Subscribe from "modals/Subscribe";
import useTalentFinderContext from "contexts/TalentFinder/useTalentFinder";
import Ordering from "./Ordering";
import ManualFilters from "./ManualFilters";
import {Search} from "lucide-react";
import TalentFinderLocker from "./Locked";
import TalentFinderOnboarding, {TF_ONBOARDED_KEY} from "./Onboarding";

const TalentFinderPage: FC = () => {
  let {page, pages, setPage, loading, talents, scroll, total} = useTalentFinderContext();

  const [sp, setSp] = useSearchParams();
  const [search, setSearch] = useState(sp.get("search") || "");
  const type = sp.get("type");
  const [requireSub, SubscribeModal] = useAwaitableModal(Subscribe, {} as any);
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchChangeTimeout = useRef<NodeJS.Timeout>();

  const user = useRecruiter();
  const [onboardingDone, setOnboardingDone] = useState(() => !!localStorage.getItem(TF_ONBOARDED_KEY));

  const canUseTop = ["ultimate"].includes(user.subscription?.plan as string);
  const canUseRising = ["premium", "ultimate"].includes(user.subscription?.plan as string);

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    if (searchChangeTimeout.current) clearTimeout(searchChangeTimeout.current);
    setSearch(e.target.value);
    searchChangeTimeout.current = setTimeout(() => {
      setSp((old) => {
        old.set("search", e.target.value);
        return old;
      });
    }, 500);
  };

  const setManual = () =>
    setSp((old) => {
      old.delete("type");
      return old;
    });

  const setTop = () =>
    setSp((old) => {
      old.set("type", "top");
      return old;
    });

  const setRising = () =>
    setSp((old) => {
      old.set("type", "rising");
      return old;
    });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      scroll.current = el.scrollTop;
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scroll({top: scroll.current, behavior: "auto"});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRef.current]);

  // @ts-ignore
  if (user.isEnterprise && !user.hasTalentFinder) return <TalentFinderLocker />;

  return (
    <>
      {!onboardingDone && <TalentFinderOnboarding onDone={() => setOnboardingDone(true)} />}
      <div className="w-full h-full flex flex-col overflow-hidden" style={{backgroundColor: "#F5F3F0"}}>
        {SubscribeModal}

        {/* Filter bar */}
        <div
          className="px-5 py-3 border-b flex flex-wrap items-center gap-3 flex-shrink-0"
          style={{backgroundColor: "#FDFCF9", borderColor: "#E8E4DF"}}
        >
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
              style={{color: "#8490AA"}}
            />
            <input
              value={search}
              onChange={onChangeSearch}
              className="w-full pl-9 pr-3 py-2 rounded-xl text-xs border outline-none"
              style={{
                backgroundColor: "#F7F5F2",
                borderColor: "#E8E4DF",
                color: "#282D4D",
              }}
              placeholder="Search candidates…"
            />
          </div>

          {/* Type tab switcher */}
          <div className="flex items-center gap-1 p-0.5 rounded-xl" style={{backgroundColor: "#EDE9E3"}}>
            <button
              className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
              style={
                !type
                  ? {backgroundColor: "white", color: "#282D4D", boxShadow: "0px 1px 4px rgba(51,41,28,0.08)"}
                  : {backgroundColor: "transparent", color: "#8490AA"}
              }
              onClick={setManual}
            >
              All Talents
            </button>
            <Tooltip text={canUseTop ? "Displays Top Talent Only" : "Upgrade Your Plan"} position="top">
              <button
                className={
                  ["text-xs font-semibold px-3 py-1.5 rounded-lg transition-all", !canUseTop && "opacity-50"].asClass
                }
                style={
                  type === "top"
                    ? {backgroundColor: "white", color: "#282D4D", boxShadow: "0px 1px 4px rgba(51,41,28,0.08)"}
                    : {backgroundColor: "transparent", color: "#8490AA"}
                }
                onClick={canUseTop ? setTop : requireSub}
              >
                Top Talents
              </button>
            </Tooltip>
            <Tooltip text={canUseRising ? "See Candidates Upskilling The Fastest" : "Upgrade Your Plan"} position="top">
              <button
                className={
                  ["text-xs font-semibold px-3 py-1.5 rounded-lg transition-all", !canUseRising && "opacity-50"].asClass
                }
                style={
                  type === "rising"
                    ? {backgroundColor: "white", color: "#282D4D", boxShadow: "0px 1px 4px rgba(51,41,28,0.08)"}
                    : {backgroundColor: "transparent", color: "#8490AA"}
                }
                onClick={canUseRising ? setRising : requireSub}
              >
                Rising Talents
              </button>
            </Tooltip>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <Ordering />
            <ManualFilters />
          </div>
        </div>

        {/* Card list */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          {talents === undefined ? (
            Array.from({length: 5}).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl overflow-hidden animate-pulse"
                style={{border: "1px solid #E8E4DF"}}
              >
                {/* metric bar skeleton */}
                <div className="flex h-1.5">
                  {Array.from({length: 4}).map((_, j) => (
                    <div key={j} className="flex-1 bg-brand-cream-dark" />
                  ))}
                </div>
                {/* body skeleton */}
                <div className="px-5 py-4 flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-brand-cream-dark flex-shrink-0" />
                  <div className="flex-1 space-y-2 pt-1">
                    <div className="h-4 bg-brand-cream-dark rounded w-1/3" />
                    <div className="h-3 bg-brand-cream-dark rounded w-1/2" />
                    <div className="h-3 bg-brand-cream-dark rounded w-1/4" />
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <div className="h-6 w-20 bg-brand-cream-dark rounded-full" />
                    <div className="h-6 w-24 bg-brand-cream-dark rounded-full" />
                  </div>
                </div>
                {/* footer skeleton */}
                <div className="px-5 py-3 border-t border-brand-border flex gap-2" style={{backgroundColor: "#FAFAF9"}}>
                  {Array.from({length: 4}).map((_, j) => (
                    <div key={j} className="w-9 h-9 rounded-full bg-brand-cream-dark" />
                  ))}
                </div>
              </div>
            ))
          ) : talents.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-brand-muted text-sm">No talents found</div>
          ) : (
            talents.map((talent) => <Talent key={talent.id} talent={talent} />)
          )}
        </div>

        {/* Footer pagination */}
        <div
          className="flex-shrink-0 px-5 py-3 border-t flex items-center gap-4"
          style={{backgroundColor: "white", borderColor: "#E8E4DF"}}
        >
          <span className="text-xs text-brand-muted grow">
            Total Candidates Found:{" "}
            <span className="font-semibold text-brand-navy">{loading ? "..." : (total ?? 0)}</span>
          </span>
          <Paginator page={page} setPage={setPage} loading={loading} total={pages} />
          <span className="grow" />
        </div>
      </div>
    </>
  );
};

export default TalentFinderPage;
